import ArticleCard from "@blocks/Card/ArticleCard";
import MainBlock from "@blocks/Container/MainBlock";
import WrapperBlock from "@blocks/Container/WrapperBlock";
import GELiveBlog from "@components/Layouts/GeneralElection/GELiveBlog";
import { BANNER_CONFIG, BannerEvents } from "@config/BannerConfigConstants";
import { RenderImu1AdInfiniteScroll } from "@elements/Advertisement/RenderImu1AdInfiniteScroll";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import Divider from "@elements/Divider/Divider";
import Heading from "@elements/Typography/Heading";
import fetchTMApi from "@helper/fetchTMApi";
import { displayAfter, hideAfter } from "@helper/utils";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { Fragment, ReactElement, useState } from "react";

import {
  ArticleVariantProps,
  KeywordVariantProps,
} from "./KeywordVariantHelper";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

export default function NdpVariant({
  tagAlias = BANNER_CONFIG[BannerEvents.NATIONAL_DAY_2025].event,
  tagName = BANNER_CONFIG[BannerEvents.NATIONAL_DAY_2025].title,
  sectionName,
  keywordArticles,
  showBreadcrumbs = true,
  showTitle = true,
  pageAdTargetValue,
  liveBlog = "",
  ndpVariant = "main",
}: KeywordVariantProps): ReactElement {
  const isLiveblogVariant = ndpVariant === "liveblog";

  return (
    <WrapperBlock className="flex flex-col lg:flex-row justify-between">
      <MainBlock
        sectionName={tagName}
        showBreadcrumbs={showBreadcrumbs}
        showTitle={showTitle}
        className="lg:!pr-0 lg:border-0"
      >
        {isLiveblogVariant ? (
          liveBlog && <GELiveBlog variant="body" geLiveBlogContent={liveBlog} />
        ) : (
          <NdpArticles
            tagAlias={tagAlias}
            sectionName={sectionName}
            keywordArticles={keywordArticles}
            pageAdTargetValue={pageAdTargetValue}
            placement="main"
          />
        )}
      </MainBlock>
      <div className="lg:w-4/12 lg:pr-xxs lg:pl-md min-h-[800px]">
        <div data-testid="side-block" className="w-full">
          <AdsImu2
            sectionName={sectionName}
            pageAdTargetType={pageAdTargetValue}
          />
          <Divider className="border-b border-gray-2200" />

          {isLiveblogVariant ? (
            <>
              <Heading
                Element="h2"
                className="text-red-450 text-2.5md font-bold leading-6 mt-5"
              >
                செய்திகள்
              </Heading>
              <NdpArticles
                tagAlias={tagAlias}
                sectionName={sectionName}
                keywordArticles={keywordArticles}
                pageAdTargetValue={pageAdTargetValue}
                placement="sidebar"
              />
            </>
          ) : (
            liveBlog && (
              <>
                <Heading
                  Element="h2"
                  className="text-red-450 text-2.5md font-bold leading-6 mt-5"
                >
                  நேரடித் தகவல்
                </Heading>
                <GELiveBlog variant="sidebar" geLiveBlogContent={liveBlog} />
              </>
            )
          )}
        </div>
      </div>
    </WrapperBlock>
  );
}

const NdpArticles = ({
  tagAlias,
  sectionName,
  keywordArticles,
  pageAdTargetValue,
  placement = "main",
}: ArticleVariantProps) => {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = keywordArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(keywordArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? keywordArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: tagAlias,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        tagAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "5",
      }).toString();
      const response: ProcessedArticleData[] = await fetchTMApi(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      return response;
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };

  const sideBarOverrideStyle = {
    cardWrapper: "w-full lg:w-full flex-row-reverse pb-5",
    titleStyle:
      "font-semibold font-primary text-sm2 text-gray-100 hover:text-gray-100",
    imageWrapperStyle: "w-40 max-w-44 lg:w-40 h-23 max-sm:w-23",
    contentWrapper: "gap-xs",
    titleWrapper: "gap-0",
    imageStyle: "w-full h-23",
  };
  const overrideStyle = ({ index }: { index: number }) => {
    return {
      cardWrapper:
        (displayAfter(index + 1, PAGE_SIZE)
          ? "border-none"
          : "flex-col lg:flex-row") +
        (hideAfter(index, PAGE_SIZE) ? " lg:flex-row flex-row-reverse" : ""),
      titleStyle: hideAfter(index, PAGE_SIZE)
        ? "font-semibold text-2sm text-gray-100 hover:text-gray-100 lg:text-2lg lg:text-red-100 lg:hover:text-red-200"
        : "text-md lg:text-2lg",
      paragraphStyle: hideAfter(index, PAGE_SIZE) ? "hidden lg:block" : "",
      imageWrapperStyle: hideAfter(index, PAGE_SIZE)
        ? "w-31.5 h-23 lg:h-auto"
        : "",
      imageStyle: hideAfter(index, PAGE_SIZE) ? "h-23 lg:h-auto" : "",
    };
  };

  return (
    <>
      {[...keywordArticles, ...loadMoreData].map((section, index) => (
        <Fragment key={section.id}>
          <ArticleCard
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            key={section.id}
            displayWidth={placement == "sidebar" ? 175 : 260}
            displayHeight={placement == "sidebar" ? 90 : 192}
            elements={
              placement == "sidebar"
                ? { hasParagraph: false, hasDate: true }
                : { hasDate: true }
            }
            {...section}
            overrideStyle={
              placement == "sidebar"
                ? sideBarOverrideStyle
                : overrideStyle({ index })
            }
          />
          {displayAfter(index + 1, PAGE_SIZE) && (
            <>
              {RenderImu1AdInfiniteScroll(
                (index + 1) / PAGE_SIZE,
                sectionName ?? "",
                pageAdTargetValue,
              )}
            </>
          )}
        </Fragment>
      ))}
      {showLoadMore ? (
        <div className="flex w-full justify-center mb-xl">
          <LoadMore
            rootClassName="my-4 w-full"
            onLoadMore={handleLoadMore}
            hasMore={hasMoreStories}
            loadText="Load More"
            mobileInfiniteScroll={true}
            pageNumber={pageNumber}
            sectionName={sectionName}
          />
        </div>
      ) : null}
    </>
  );
};
